import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  FacebookLogo, InstagramLogo, TiktokLogo, WhatsappLogo, ChatCircleDots,
  Plus, PaperPlaneTilt, Trash, ArrowClockwise, Lightning, ChartLineUp, ChatCircle, CursorClick,
} from "@phosphor-icons/react";
import { PageHeader, EmptyState } from "../components/ui/kaizen";

const PLATFORM_ICON = {
  facebook: FacebookLogo, messenger: FacebookLogo, instagram: InstagramLogo,
  tiktok: TiktokLogo, whatsapp: WhatsappLogo, autre: ChatCircleDots,
};
const PLATFORM_COLOR = {
  facebook: "#1877F2", messenger: "#1877F2", instagram: "#E1306C",
  tiktok: "#69C9D0", whatsapp: "#25D366", autre: "#a1a1aa",
};
const STATUS_COLOR = {
  nouveau: "#3b82f6", a_repondre: "#f59e0b", repondu: "#22c55e",
  attente_client: "#a855f7", a_relancer: "#ef4444", converti: "#10b981",
  support: "#f97316", ferme: "#71717a",
};

function PlatformBadge({ platform }) {
  const Icon = PLATFORM_ICON[platform] || ChatCircleDots;
  return <Icon size={16} weight="fill" style={{ color: PLATFORM_COLOR[platform] || "#a1a1aa" }} />;
}

export default function SocialInboxModule() {
  const [meta, setMeta] = useState({ platforms: [], statuses: [], sources: [] });
  const [stats, setStats] = useState(null);
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ platform: "", status: "", q: "" });
  const [quickReplies, setQuickReplies] = useState([]);
  const [draft, setDraft] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiIntent, setAiIntent] = useState(null);
  const [convertBusy, setConvertBusy] = useState(false);
  const [autoCfg, setAutoCfg] = useState(null);
  const [showAutoCfg, setShowAutoCfg] = useState(false);
  const [react, setReact] = useState(null);
  const [showReact, setShowReact] = useState(false);

  const loadList = useCallback(() => {
    const params = {};
    if (filters.platform) params.platform = filters.platform;
    if (filters.status) params.status = filters.status;
    if (filters.q) params.q = filters.q;
    api.get("/social-inbox/conversations", { params }).then((r) => setConvos(r.data.conversations || [])).catch(() => {});
    api.get("/social-inbox/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => {
    api.get("/social-inbox/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/social-inbox/quick-replies").then((r) => setQuickReplies(r.data.quick_replies || [])).catch(() => {});
    api.get("/social-inbox/auto-send/settings").then((r) => setAutoCfg(r.data)).catch(() => {});
    api.get("/social-inbox/reactivity").then((r) => setReact(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openConvo = async (id) => {
    try { const r = await api.get(`/social-inbox/conversations/${id}`); setSelected(r.data); setDraft(""); }
    catch { toast.error("Conversation introuvable"); }
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    try { const r = await api.patch(`/social-inbox/conversations/${selected.id}`, { status }); setSelected(r.data); loadList(); }
    catch { toast.error("Erreur"); }
  };

  const patchField = async (field, value) => {
    if (!selected) return;
    try { const r = await api.patch(`/social-inbox/conversations/${selected.id}`, { [field]: value }); setSelected(r.data); loadList(); }
    catch { toast.error("Erreur"); }
  };

  const sendMessage = async (direction) => {
    if (!selected || !draft.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/social-inbox/conversations/${selected.id}/messages`, { direction, text: draft.trim() }); setSelected(r.data); setDraft(""); loadList(); }
    catch { toast.error("Erreur d'envoi"); }
    finally { setBusy(false); }
  };

  const removeConvo = async (id) => {
    try { await api.delete(`/social-inbox/conversations/${id}`); toast.success("Conversation supprimée"); if (selected?.id === id) setSelected(null); loadList(); }
    catch { toast.error("Erreur"); }
  };

  const aiSuggest = async () => {
    if (!selected) return;
    setAiBusy(true); setAiIntent(null);
    try {
      const r = await api.post(`/social-inbox/conversations/${selected.id}/ai-suggest`);
      setDraft(r.data.suggested_reply || "");
      setAiIntent({ intent: r.data.intent, priority: r.data.priority });
      toast.success("Suggestion IA prête — relis avant d'envoyer.");
    } catch (e) { toast.error(e.response?.data?.detail || "IA indisponible"); }
    finally { setAiBusy(false); }
  };

  const convertLead = async () => {
    if (!selected) return;
    setConvertBusy(true);
    try {
      const r = await api.post(`/social-inbox/conversations/${selected.id}/convert-to-lead`);
      toast.success("Converti en lead CRM ✓ — lien cours d'essai (19,98 $) prêt à envoyer.");
      const conv = await api.get(`/social-inbox/conversations/${selected.id}`);
      setSelected(conv.data); loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setConvertBusy(false); }
  };

  const statusLabel = (v) => meta.statuses.find((s) => s.value === v)?.label || v;

  const saveAutoCfg = async (patch) => {
    try {
      const r = await api.put("/social-inbox/auto-send/settings", patch);
      setAutoCfg(r.data);
      toast.success("Réglage envoi auto enregistré");
    } catch { toast.error("Erreur"); }
  };
  const toggleAutoPlatform = (p) => {
    const cur = autoCfg?.auto_send_platforms || [];
    saveAutoCfg({ auto_send_platforms: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] });
  };
  const toggleSafeIntent = (i) => {
    const cur = autoCfg?.safe_intents || [];
    saveAutoCfg({ safe_intents: cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i] });
  };
  const saveStaleCfg = async (patch) => {
    try {
      const r = await api.put("/social-inbox/auto-send/settings", patch);
      setAutoCfg(r.data);
      const rr = await api.get("/social-inbox/reactivity");
      setReact(rr.data);
      toast.success("Seuil mis à jour");
    } catch { toast.error("Erreur"); }
  };
  const saveBusinessHours = (dayKey, patch) => {
    const bh = { ...(autoCfg?.business_hours || {}) };
    const cur = bh[dayKey] || { open: "16:00", close: "21:00" };
    bh[dayKey] = patch === null ? null : { ...cur, ...patch };
    saveAutoCfg({ business_hours: bh });
  };
  const holdAutosend = async () => {
    if (!selected) return;
    try {
      await api.post(`/social-inbox/conversations/${selected.id}/hold-autosend`);
      const conv = await api.get(`/social-inbox/conversations/${selected.id}`);
      setSelected(conv.data); loadList();
      toast.success("Envoi auto retenu — à toi de répondre.");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="social-inbox-page" className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="px-8 pt-6">
        <PageHeader
          title="Inbox unifiée"
          icon={ChatCircleDots}
          breadcrumbs={[{ label: "CRM & relations" }, { label: "Inbox sociale" }]}
          subtitle="Centralise et suis tes conversations Facebook, Instagram, TikTok et WhatsApp."
          actions={
            <>
              <button data-testid="reactivity-btn" onClick={() => setShowReact((v) => !v)}
                className="px-4 py-2.5 rounded-lg font-bold flex items-center gap-2 transition border bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-amber-500/50">
                <ChartLineUp size={16} weight="bold" /> Réactivité
              </button>
              <button data-testid="autosend-settings-btn" onClick={() => setShowAutoCfg((v) => !v)}
                className={`px-4 py-2.5 rounded-lg font-bold flex items-center gap-2 transition border ${autoCfg?.auto_send_enabled ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                <Lightning size={16} weight="fill" /> Envoi auto {autoCfg?.auto_send_enabled ? "· ON" : "· OFF"}
              </button>
              <button data-testid="new-conversation-btn" onClick={() => setShowNew(true)}
                className="px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold flex items-center gap-2 transition active:scale-[0.98]">
                <Plus size={18} weight="bold" /> Nouvelle conversation
              </button>
            </>
          }
        />
      </div>

      {/* Tableau de bord Réactivité */}
      {showReact && react && (
        <div data-testid="reactivity-panel" className="px-8 py-4 border-b border-zinc-800 bg-zinc-900/40">
          <div className="text-sm font-bold text-amber-300 flex items-center gap-2 mb-3"><ChartLineUp size={15} weight="bold" /> Réactivité — 30 derniers jours</div>
          {react.stale_count > 0 && (
            <div data-testid="stale-alert" className="mb-3 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3">
              <div className="text-sm font-bold text-rose-300 mb-1.5">⚠️ {react.stale_count} conversation{react.stale_count > 1 ? "s" : ""} en attente depuis +2h</div>
              <div className="flex flex-wrap gap-1.5">
                {react.stale.map((s) => (
                  <button key={s.id} data-testid={`stale-item-${s.id}`} onClick={() => openConvo(s.id)}
                    className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-rose-500/30 text-zinc-200">
                    {s.contact_name || "Contact"} · <span className="text-rose-300 font-semibold">{s.hours_waiting}h</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { l: "Temps 1ère réponse (moy.)", v: react.avg_first_response_minutes != null ? `${react.avg_first_response_minutes} min` : "—", c: react.avg_first_response_minutes == null ? "#71717a" : react.avg_first_response_minutes < 10 ? "#22c55e" : react.avg_first_response_minutes <= 60 ? "#f59e0b" : "#ef4444", testid: "react-avg" },
              { l: "Médiane 1ère réponse", v: react.median_first_response_minutes != null ? `${react.median_first_response_minutes} min` : "—", c: "#fbbf24", testid: "react-median" },
              { l: "Taux de réponse", v: `${react.response_rate}%`, c: "#22c55e", testid: "react-resprate" },
              { l: "% auto-répondu", v: `${react.auto_reply_rate}%`, c: "#10b981", testid: "react-autorate" },
              { l: "Leads Inbox (7j)", v: react.leads_from_inbox_7d, c: "#3b82f6", testid: "react-leads7" },
              { l: "En retard (+2h)", v: react.stale_count ?? 0, c: react.stale_count > 0 ? "#ef4444" : "#22c55e", testid: "react-stale" },
            ].map((s) => (
              <div key={s.testid} data-testid={s.testid} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                <div className="text-2xl font-black" style={{ color: s.c }}>{s.v}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">🟢 &lt; 10 min · 🟠 10–60 min · 🔴 &gt; 1h pour la 1ère réponse. Sur {react.total_conversations} conversations · {react.leads_from_inbox_30d} leads générés (30j). Plus tu réponds vite, plus tu convertis. 🎯</p>
          {autoCfg && (
            <div data-testid="stale-config" className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-zinc-400">Seuil « en retard » :</span>
                <input type="number" min="5" max="1440" data-testid="stale-threshold-input" defaultValue={autoCfg.stale_threshold_min ?? 120}
                  onBlur={(e) => saveStaleCfg({ stale_threshold_min: parseInt(e.target.value) || 120 })}
                  className="w-20 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-sm" />
                <span className="text-zinc-500">min</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="stale-relax-toggle">
                <input type="checkbox" checked={autoCfg.stale_relax_offhours !== false}
                  onChange={(e) => saveStaleCfg({ stale_relax_offhours: e.target.checked })} className="accent-amber-500 w-4 h-4" />
                <span className="text-zinc-400">Plus tolérant la nuit (21h–9h) & week-end</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Réglages envoi auto */}
      {showAutoCfg && autoCfg && (
        <div data-testid="autosend-panel" className="px-8 py-4 border-b border-zinc-800 bg-zinc-900/40 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-bold text-amber-300 flex items-center gap-2"><Lightning size={15} weight="fill" /> Envoi automatique « première touche »</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="autosend-enable-toggle">
              <input type="checkbox" checked={!!autoCfg.auto_send_enabled} onChange={(e) => saveAutoCfg({ auto_send_enabled: e.target.checked })} className="accent-emerald-500 w-4 h-4" />
              <span className={autoCfg.auto_send_enabled ? "text-emerald-300 font-semibold" : "text-zinc-400"}>{autoCfg.auto_send_enabled ? "Activé" : "Désactivé"}</span>
            </label>
          </div>
          <p className="text-xs text-zinc-500">Quand un nouveau message arrive avec une intention SÛRE, le brouillon IA part automatiquement après le délai — sauf si tu cliques « Retenir ». Les sujets sensibles (prix, plaintes, privés) restent toujours en brouillon.</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase text-zinc-500 mb-1">Plateformes</div>
              <div className="flex gap-1.5">
                {["facebook", "instagram"].map((p) => (
                  <button key={p} data-testid={`autosend-platform-${p}`} onClick={() => toggleAutoPlatform(p)}
                    className={`text-xs px-2.5 py-1 rounded-full border capitalize ${(autoCfg.auto_send_platforms || []).includes(p) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-400"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-zinc-500 mb-1">Intentions sûres (auto)</div>
              <div className="flex gap-1.5 flex-wrap">
                {["horaire", "info", "essai"].map((i) => (
                  <button key={i} data-testid={`autosend-intent-${i}`} onClick={() => toggleSafeIntent(i)}
                    className={`text-xs px-2.5 py-1 rounded-full border ${(autoCfg.safe_intents || []).includes(i) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-400"}`}>{i}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-zinc-500 mb-1">Délai (sec) avant envoi</div>
              <input type="number" min="10" max="900" data-testid="autosend-delay" defaultValue={autoCfg.delay_seconds}
                onBlur={(e) => saveAutoCfg({ delay_seconds: parseInt(e.target.value) || 60 })}
                className="w-24 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-sm" />
            </div>
          </div>
          <div className="pt-3 mt-1 border-t border-zinc-800 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="offline-reply-toggle">
              <input type="checkbox" checked={!!autoCfg.offline_reply_enabled} onChange={(e) => saveAutoCfg({ offline_reply_enabled: e.target.checked })} className="accent-emerald-500 w-4 h-4" />
              <span className="font-semibold text-zinc-200">Réponse auto « hors ligne »</span>
              <span className="text-xs text-zinc-500">— accusé instantané quand un DM arrive la nuit (21h–9h) ou le week-end</span>
            </label>
            {autoCfg.offline_reply_enabled && (
              <textarea data-testid="offline-reply-message" defaultValue={autoCfg.offline_reply_message}
                onBlur={(e) => saveAutoCfg({ offline_reply_message: e.target.value })} rows={2}
                className="w-full px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-sm text-zinc-200"
                placeholder="Message envoyé automatiquement hors heures d'ouverture…" />
            )}
          </div>
          <div className="pt-3 mt-1 border-t border-zinc-800 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="auto-relance-toggle">
              <input type="checkbox" checked={!!autoCfg.auto_relance_enabled} onChange={(e) => saveAutoCfg({ auto_relance_enabled: e.target.checked })} className="accent-emerald-500 w-4 h-4" />
              <span className="font-semibold text-zinc-200">⚡ Relance auto des prospects chauds</span>
              <span className="text-xs text-zinc-500">— relance l'IA toute seule un prospect qui a cliqué le lien mais n'a pas réservé</span>
            </label>
            {autoCfg.auto_relance_enabled && (
              <div className="flex items-center gap-2 text-sm text-zinc-300 pl-6">
                Relancer après
                <input type="number" min="1" max="168" data-testid="auto-relance-delay" defaultValue={autoCfg.auto_relance_delay_hours ?? 24}
                  onBlur={(e) => saveAutoCfg({ auto_relance_delay_hours: parseInt(e.target.value) || 24 })}
                  className="w-20 px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-sm" />
                heures sans réservation (envoi réel via Meta uniquement)
              </div>
            )}
          </div>
          <div className="pt-3 mt-1 border-t border-zinc-800" data-testid="business-hours-editor">
            <div className="text-[11px] uppercase text-zinc-500 mb-2">Heures d'ouverture (réception) — pilote le « hors ligne » & le « en retard »</div>
            <div className="space-y-1">
              {[["0","Lun"],["1","Mar"],["2","Mer"],["3","Jeu"],["4","Ven"],["5","Sam"],["6","Dim"]].map(([k, lbl]) => {
                const bh = (autoCfg.business_hours || {})[k];
                const closed = !bh || !bh.open || !bh.close;
                return (
                  <div key={k} className="flex items-center gap-2 text-sm" data-testid={`bh-row-${k}`}>
                    <span className="w-10 text-zinc-400">{lbl}</span>
                    <label className="flex items-center gap-1 text-xs text-zinc-500 cursor-pointer w-20">
                      <input type="checkbox" checked={!closed} onChange={(e) => saveBusinessHours(k, e.target.checked ? { open: "16:00", close: "21:00" } : null)} className="accent-amber-500" />
                      {closed ? "Fermé" : "Ouvert"}
                    </label>
                    {!closed && (
                      <>
                        <input type="time" data-testid={`bh-open-${k}`} value={bh.open} onChange={(e) => saveBusinessHours(k, { open: e.target.value })} className="px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-xs" />
                        <span className="text-zinc-600">→</span>
                        <input type="time" data-testid={`bh-close-${k}`} value={bh.close} onChange={(e) => saveBusinessHours(k, { close: e.target.value })} className="px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-xs" />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}

      {/* Stats */}
      <div className="px-8 py-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { l: "Ouvertes", v: stats?.open ?? 0, c: "#f59e0b", t: "open" },
          { l: "À relancer (dû)", v: stats?.follow_up_due ?? 0, c: "#ef4444", t: "follow" },
          { l: "Converti", v: stats?.by_status?.converti ?? 0, c: "#10b981", t: "converti" },
          { l: "À répondre", v: stats?.by_status?.a_repondre ?? 0, c: "#f59e0b", t: "arepondre" },
          { l: "Support", v: stats?.by_status?.support ?? 0, c: "#f97316", t: "support" },
          { l: "Total", v: stats?.total ?? 0, c: "#fff", t: "total" },
        ].map((s) => (
          <div key={s.t} data-testid={`inbox-stat-${s.t}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="text-2xl font-black" style={{ color: s.c }}>{s.v}</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="px-8 pb-4 flex items-center gap-2 flex-wrap">
        <input data-testid="inbox-search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          placeholder="Rechercher nom, pseudo, sujet…" className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm w-64 outline-none focus:border-amber-500" />
        <select data-testid="inbox-filter-platform" value={filters.platform} onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}
          className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none">
          <option value="">Toutes plateformes</option>
          {meta.platforms.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="inbox-filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none">
          <option value="">Tous statuts</option>
          {meta.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button onClick={loadList} className="px-3 py-2 rounded-lg border border-zinc-800 text-sm hover:bg-zinc-900 flex items-center gap-1.5"><ArrowClockwise size={14} /> Actualiser</button>
      </div>

      {/* Master-detail */}
      <div className="px-8 pb-10 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* List */}
        <div className="space-y-2" data-testid="inbox-list">
          {convos.length === 0 && (
            <EmptyState
              icon={ChatCircle}
              title="Aucune conversation"
              description="Ajoute-en une avec « Nouvelle conversation », ou laisse l'ingestion automatique des DM remplir ta boîte."
              actionLabel="Nouvelle conversation"
              onAction={() => setShowNew(true)}
            />
          )}
          {convos.map((c) => (
            <button key={c.id} data-testid={`convo-item-${c.id}`} onClick={() => openConvo(c.id)}
              className={`w-full text-left bg-zinc-900 border rounded-xl p-3 transition hover:border-amber-500/50 ${selected?.id === c.id ? "border-amber-500" : "border-zinc-800"}`}>
              <div className="flex items-center gap-2 mb-1">
                <PlatformBadge platform={c.platform} />
                <span className="font-semibold text-sm truncate flex-1">{c.contact_name}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${STATUS_COLOR[c.status]}22`, color: STATUS_COLOR[c.status] }}>{statusLabel(c.status)}</span>
              </div>
              <div className="text-xs text-zinc-500 truncate">{c.last_inbound || c.subject || "—"}</div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div data-testid="inbox-detail">
          {!selected && (
            <EmptyState
              icon={CursorClick}
              title="Sélectionne une conversation"
              description="Choisis une conversation à gauche pour la lire, suivre son statut et y répondre."
            />
          )}
          {selected && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2"><PlatformBadge platform={selected.platform} /><span className="font-bold text-lg">{selected.contact_name}</span></div>
                  <div className="text-xs text-zinc-500">{selected.contact_handle} · source: {selected.source}</div>
                  {selected.profile_url && <a href={selected.profile_url} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:underline">Voir le profil →</a>}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="convo-convert-lead" onClick={convertLead} disabled={convertBusy || selected.status === "converti"}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold disabled:opacity-50">
                    {selected.status === "converti" ? "✓ Converti" : (convertBusy ? "…" : "Convertir en lead")}
                  </button>
                  <button data-testid="convo-delete" onClick={() => removeConvo(selected.id)} className="text-zinc-500 hover:text-red-400"><Trash size={18} /></button>
                </div>
              </div>

              {/* Pipeline */}
              <div className="flex flex-wrap gap-1.5" data-testid="convo-pipeline">
                {meta.statuses.map((s) => (
                  <button key={s.value} data-testid={`status-btn-${s.value}`} onClick={() => updateStatus(s.value)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${selected.status === s.value ? "border-transparent text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
                    style={selected.status === s.value ? { background: STATUS_COLOR[s.value] } : {}}>
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Meta fields */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">Responsable</span>
                  <input data-testid="convo-assignee" defaultValue={selected.assignee} onBlur={(e) => patchField("assignee", e.target.value)}
                    className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 outline-none text-sm" placeholder="Qui suit ?" /></label>
                <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">Relance le</span>
                  <input data-testid="convo-followup" type="date" defaultValue={selected.follow_up_date} onChange={(e) => patchField("follow_up_date", e.target.value)}
                    className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 outline-none text-sm" /></label>
              </div>

              {/* History */}
              <div className="max-h-64 overflow-y-auto space-y-2 bg-zinc-950/60 rounded-lg p-3" data-testid="convo-history">
                {(selected.messages || []).length === 0 && <div className="text-xs text-zinc-600 italic">Aucun message enregistré.</div>}
                {(selected.messages || []).map((m, i) => (
                  <div key={i} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === "out" ? "ml-auto bg-amber-500/15 text-amber-100" : "bg-zinc-800 text-zinc-200"}`}>
                    <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">{m.direction === "out" ? "Envoyé" : "Reçu"}</div>
                    {m.text}
                  </div>
                ))}
              </div>

              {/* Brouillon IA — 1ère touche automatique (ingestion webhook) */}
              {selected.ai_draft && (
                <div data-testid="ai-first-touch-panel" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
                  <div className="text-xs font-semibold text-amber-300 flex items-center flex-wrap gap-1.5">
                    <Lightning size={13} weight="fill" /> Brouillon IA — 1ère touche
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-300">intention : <b>{selected.ai_draft.intent}</b></span>
                    {selected.auto_lead && (
                      <span data-testid="ai-autolead-badge" className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">✓ Lead CRM créé</span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap bg-zinc-950/50 rounded p-2">{selected.ai_draft.suggested_reply}</div>
                  <button data-testid="use-ai-draft-btn" onClick={() => setDraft(selected.ai_draft.suggested_reply)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40">
                    Utiliser ce brouillon ↓
                  </button>
                  {selected.auto_send_status === "scheduled" && (
                    <div data-testid="autosend-scheduled" className="flex items-center gap-2 mt-1 text-xs text-sky-300">
                      ⏳ Envoi auto programmé
                      <button data-testid="hold-autosend-btn" onClick={holdAutosend}
                        className="px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25">Retenir</button>
                    </div>
                  )}
                  {selected.auto_send_status === "sent" && <div data-testid="autosend-sent" className="mt-1 text-xs text-emerald-300">✅ Réponse envoyée automatiquement</div>}
                  {selected.auto_send_status === "held" && <div data-testid="autosend-held" className="mt-1 text-xs text-zinc-400">⏸️ Envoi auto retenu</div>}
                  {selected.auto_send_status === "failed" && <div data-testid="autosend-failed" className="mt-1 text-xs text-rose-300">⚠️ Échec de l'envoi auto — réponds manuellement.</div>}
                </div>
              )}

              {/* Quick replies + IA */}
              <div className="flex flex-wrap items-center gap-1.5" data-testid="quick-replies">
                <button data-testid="ai-suggest-btn" onClick={aiSuggest} disabled={aiBusy}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 flex items-center gap-1 disabled:opacity-50">
                  <Lightning size={11} weight="fill" /> {aiBusy ? "IA…" : "✨ Suggestion IA"}
                </button>
                {aiIntent && (
                  <span data-testid="ai-intent-badge" className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">
                    intention: <b>{aiIntent.intent}</b>{aiIntent.priority === "urgent" ? " · ⚠️ urgent" : ""}
                  </span>
                )}
                {quickReplies.map((qr) => (
                  <button key={qr.id} onClick={() => setDraft(qr.text)} title={qr.text}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center gap-1">
                    <Lightning size={11} weight="fill" className="text-amber-400" /> {qr.title}
                  </button>
                ))}
              </div>

              {/* Composer */}
              <div className="space-y-2">
                <textarea data-testid="convo-draft" value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                  placeholder="Rédige ta réponse, ou note le message reçu…" className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 outline-none text-sm focus:border-amber-500" />
                <div className="flex gap-2">
                  <button data-testid="log-inbound" onClick={() => sendMessage("in")} disabled={busy || !draft.trim()}
                    className="px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm hover:bg-zinc-800 disabled:opacity-40">+ Message reçu</button>
                  <button data-testid="log-outbound" onClick={() => sendMessage("out")} disabled={busy || !draft.trim()}
                    className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm flex items-center gap-1.5 disabled:opacity-40">
                    <PaperPlaneTilt size={15} weight="fill" /> J'ai répondu
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showNew && <NewConversationModal meta={meta} onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); loadList(); openConvo(c.id); }} />}
    </div>
  );
}

function NewConversationModal({ meta, onClose, onCreated }) {
  const [form, setForm] = useState({ platform: "facebook", contact_name: "", contact_handle: "", profile_url: "", source: "dm", subject: "", first_message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.contact_name.trim()) { toast.error("Nom du contact requis"); return; }
    setBusy(true);
    try { const r = await api.post("/social-inbox/conversations", form); toast.success("Conversation ajoutée"); onCreated(r.data); }
    catch { toast.error("Erreur de création"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="new-convo-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-lg space-y-3">
        <h2 className="text-xl font-bold">Nouvelle conversation</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-zinc-500">Plateforme</span>
            <select data-testid="new-platform" value={form.platform} onChange={(e) => set("platform", e.target.value)} className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm">
              {meta.platforms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-zinc-500">Source</span>
            <select value={form.source} onChange={(e) => set("source", e.target.value)} className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm">
              {meta.sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></label>
        </div>
        <input data-testid="new-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Nom du contact *" className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm outline-none focus:border-amber-500" />
        <div className="grid grid-cols-2 gap-3">
          <input value={form.contact_handle} onChange={(e) => set("contact_handle", e.target.value)} placeholder="Pseudo / @handle" className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm outline-none" />
          <input value={form.profile_url} onChange={(e) => set("profile_url", e.target.value)} placeholder="Lien du profil" className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm outline-none" />
        </div>
        <input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Sujet (ex: demande tarifs)" className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm outline-none" />
        <textarea value={form.first_message} onChange={(e) => set("first_message", e.target.value)} rows={3} placeholder="Premier message reçu (optionnel)" className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm outline-none" />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-700 text-sm hover:bg-zinc-800">Annuler</button>
          <button data-testid="new-convo-submit" onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm disabled:opacity-50">{busy ? "…" : "Ajouter"}</button>
        </div>
      </div>
    </div>
  );
}
